import { BasePagination } from '@elkaso-app/web-design';
import { useGetLicensesApi } from 'apis/restaurants/restaurants-licenses/use-get-licenses-api';
import { BaseTable, BaseTableContainer, RestaurantIdFilterButton } from 'components/base-table';
import { CustomTableCell, TableCell, TableHeader } from 'components/base-table/components';
import { ability } from 'configs/ability';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { DateTimeCell, IDateTimeCell } from 'pages/restaurants/components/restaurant-licenses/date-time-cell';
import React from 'react';
import { CellProps } from 'react-table';
import { PendingRequestActionsCell, PendingRequestActionsCellProps } from './pending-request-actions-cell';

export const PendingRequestList = () => {
  const setPaginationParams = useSetPaginationParamsV1();

  const { isLoading, isFetching, data } = useGetLicensesApi(['pending']);
  const { pageCount, total, data: licensesData } = data ?? {};

  const columns = React.useMemo(
    () => [
      {
        Header: <TableHeader name='Restaurant Name' filterButtonComponent={<RestaurantIdFilterButton />} />,
        accessor: 'restaurantName',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['restaurantName']>) => (
          <TableCell value={value} fontWeight='semibold' color='gray.600' />
        ),
      },
      {
        Header: <TableHeader name='Submission Date' />,
        accessor: 'submissionDate',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['submissionDate']>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value} />
        ),
      },
      {
        Header: <TableHeader name='Collected From' />,
        accessor: 'collectedFrom',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['collectedFrom']>) => (
          <TableCell value={value === 'in_app' ? 'Marketplace' : 'Register'} />
        ),
      },
      {
        Header: <TableHeader name='Country' />,
        accessor: 'countryCode',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['countryCode']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Type' />,
        accessor: 'type',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['type']>) => (
          <TableCell value={value.toUpperCase()} fontWeight='semibold' color='gray.600' />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <CustomTableCell<PendingRequestActionsCellProps> as={PendingRequestActionsCell} actions={value} />
        ),
      },
    ],
    []
  );

  interface IColumnsAccessorData {
    id: number;
    restaurantName: string;
    submissionDate: string;
    collectedFrom: string;
    actions: any;
    countryCode: string;
    type: string;
  }

  const columnsAccessorData = React.useMemo(
    () =>
      licensesData?.map((license: Record<string, any>): IColumnsAccessorData => {
        return {
          id: license?.id,
          restaurantName: license?.restaurant?.name,
          submissionDate: license?.createdAt,
          collectedFrom: license?.collectedFrom,
          actions: license,
          countryCode: license?.restaurant?.countryCode,
          type: license?.type,
        };
      }),
    [licensesData]
  );

  const setInitialHiddenColumns = () => {
    const hiddenColumns = [];

    if (ability.cannot('update', 'User')) {
      hiddenColumns.push('activation');
      hiddenColumns.push('actions');
    }

    return hiddenColumns;
  };

  return (
    <BaseTableContainer isLoading={isLoading || isFetching} pt={'md'} border={0}>
      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={columnsAccessorData} initialHiddenColumns={setInitialHiddenColumns()} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};
